import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import InterfaceContext, { PublicConfigurationContext, UserInfoContext } from "./Context";
import { useLocalizedStrings } from "./Localization";
import { InfoDict } from "./Questions/Props";
import { useModularMarkdown } from "./Hooks/ModularMarkdown";
import * as v0 from "@aidkitorg/types/lib/survey";
import { ResizablePanelControlContext } from "./Components/ThreeColumnPage";
import { DropdownMenu, Modal, Button, Icon, useToast, Select, SelectOptionType, AidKitLogo } from '@aidkitorg/component-library';
import { ReviewConfiguration } from '@aidkitorg/types/lib/translation/permissions';
import { SpacedSpinner } from "./Util";
import { usePost } from "./API";
import { RichText } from "@aidkitorg/types/lib/survey";
import { evalDistroConditional } from "@aidkitorg/types/lib/eval";
import { SubsurveyList } from "./Components/ApplicantSearch";
import { ApplicantHeaderCard } from "./DistroReview/ApplicantHeaderCard";
import { ApplicantContext } from "./Contexts/ApplicantContext";
import { ApplicantPaymentPage } from "./Applicant/PaymentPage";
import { ConditionalReassign, ReassignReviewer } from '@aidkitorg/types/src/survey';

/** User profile for review assignee lookup (from `/user/list`). */
export type AssigneeProfile = {
  name: string;
  email: string;
  organization_name?: string | null;
};

type ResolvedReassign = {
  assigneeField: string;
  limitAssigneeOptionsToTags?: string[];
};

/**
 * Recursively evaluates a ConditionalReassign tree against the applicant's info,
 * returning the resolved ReassignReviewer leaf.
 */
function resolveConditional(
  node: ConditionalReassign | ReassignReviewer,
  info: Record<string, string | undefined>
): ReassignReviewer | undefined {
  if (node.kind === 'Reassign Reviewer') return node;
  const conditionResult = evalDistroConditional(node.when, info);
  const branch = conditionResult ? node.then : node.otherwise;
  return resolveConditional(branch, info);
}

/**
 * Resolves a reassignReviewer config to a concrete field name and (optionally)
 * per-branch tag restrictions. If the ConditionalReassign branch defines its own
 * limitAssigneeOptionsToTags, those take priority over the outer config.
 */
export function resolveReassignDetails(
  assigneeField: string | ConditionalReassign | undefined,
  info: Record<string, any>,
  outerTags?: string[]
): ResolvedReassign | undefined {
  if (!assigneeField) return undefined;
  if (typeof assigneeField === 'string') {
    return { assigneeField, limitAssigneeOptionsToTags: outerTags };
  }
  const leaf = resolveConditional(assigneeField, info as Record<string, string | undefined>);
  if (!leaf) return undefined;
  return {
    assigneeField: leaf.assigneeField,
    limitAssigneeOptionsToTags: leaf.limitAssigneeOptionsToTags ?? outerTags,
  };
}

/**
 * Resolves an assigneeField config (which may be a plain string or a ConditionalReassign)
 * to a concrete field name string using the applicant's info.
 */
export function resolveAssigneeField(
  assigneeField: string | ConditionalReassign | undefined,
  info: Record<string, any>
): string | undefined {
  return resolveReassignDetails(assigneeField, info)?.assigneeField;
}

interface AssigneeSelectProps {
  uid: string | undefined;
  reassignReviewerConfig: {
    assigneeField: string | ConditionalReassign;
    limitAssigneeOptionsToTags?: string[];
  } | undefined;
  currentAssigneeUid: string | undefined;
  info: Record<string, any>;
}

/** Fetches possible assignees and renders an inline reassignment Select dropdown. */
export function AssigneeSelect({ uid, reassignReviewerConfig, currentAssigneeUid, info }: AssigneeSelectProps) {
  const [assigneeByUid, setAssigneeByUid] = useState<Record<string, AssigneeProfile>>({});
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [selectedUid, setSelectedUid] = useState<string | undefined>(currentAssigneeUid);
  const [reassigning, setReassigning] = useState(false);
  const location = useLocation();

  const resolvedDetails = useMemo(
    () => resolveReassignDetails(
      reassignReviewerConfig?.assigneeField,
      info,
      reassignReviewerConfig?.limitAssigneeOptionsToTags
    ),
    [reassignReviewerConfig?.assigneeField, reassignReviewerConfig?.limitAssigneeOptionsToTags, info]
  );
  const resolvedAssigneeField = resolvedDetails?.assigneeField;

  const { toast } = useToast();
  const getPossibleAssignees = usePost('/user/list');
  const reassignApplicant = usePost('/applicant/reassign');
  const L = useLocalizedStrings();

  useEffect(() => {
    setSelectedUid(currentAssigneeUid);
  }, [currentAssigneeUid]);

  useEffect(() => {
    if (!reassignReviewerConfig) {
      setAssigneeByUid({});
      setLoadingAssignees(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingAssignees(true);
      try {
        const response = await getPossibleAssignees({
          tags: resolvedDetails?.limitAssigneeOptionsToTags || [],
        });
        if (cancelled) return;
        if ((response as { error?: string })?.error) {
          toast({ description: L.review.reassign_load_error, variant: 'error' });
          setAssigneeByUid({});
        } else {
          const list = Array.isArray(response) ? response : [];
          const next: Record<string, AssigneeProfile> = {};
          for (const u of list as Array<{ uid?: string; name?: string; email?: string; organization_name?: string | null }>) {
            if (u?.uid && u.name != null && u.email != null) {
              next[u.uid] = { name: u.name, email: u.email, organization_name: u.organization_name ?? null };
            }
          }
          setAssigneeByUid(next);
        }
      } catch {
        if (!cancelled) {
          toast({ description: L.review.reassign_load_error, variant: 'error' });
          setAssigneeByUid({});
        }
      } finally {
        if (!cancelled) setLoadingAssignees(false);
      }
    })();
    return () => { cancelled = true; };
  }, [resolvedAssigneeField, resolvedDetails?.limitAssigneeOptionsToTags]);

  // This assumes we are on a subsurvey. Currently AssigneeSelect only exists on /a/:uid/subsurvey or 
  // /admin-panel/a/:uid/subsurvey, so this is a fair assumption.
  const currentSubsurvey = useMemo(() => {
    if (!uid) return undefined;
    const pathParts = location.pathname.split('/').filter(Boolean);
    const applicantBaseIndex = pathParts.indexOf('a');
    if (applicantBaseIndex === -1) return undefined;
    const uidIndex = applicantBaseIndex + 1;
    if (pathParts[uidIndex] !== uid) return undefined;
    return pathParts[uidIndex + 1] || undefined;
  }, [location.pathname, uid]);

  if (!reassignReviewerConfig || !resolvedAssigneeField) return null;

  const options: SelectOptionType<string>[] = Object.entries(assigneeByUid).map(([assigneeUid, profile]) => ({
    value: assigneeUid,
    label: profile.name,
  }));

  const selectedOption = selectedUid && assigneeByUid[selectedUid]
    ? { value: selectedUid, label: assigneeByUid[selectedUid].name }
    : undefined;

  const handleChange = async (option: SelectOptionType<string>) => {
    if (!uid || !option?.value || reassigning || !resolvedAssigneeField) return;
    setReassigning(true);
    const prevUid = selectedUid;
    setSelectedUid(option.value);
    try {
      const result = await reassignApplicant({
        applicant: uid,
        screener: option.value,
        assigneeField: resolvedAssigneeField,
        fromSubsurvey: currentSubsurvey,
        assigneeName: assigneeByUid[option.value]?.name,
      });
      if ((result as { error?: string })?.error) {
        toast({ description: L.review.reassign_failed, variant: 'error' });
        setSelectedUid(prevUid);
      }
    } catch {
      toast({ description: L.review.reassign_failed, variant: 'error' });
      setSelectedUid(prevUid);
    } finally {
      setReassigning(false);
    }
  };

  const renderAssigneeButton = (value: string) => {
    const name = assigneeByUid[value]?.name ?? value;
    return (
      <div className="flex items-center gap-2">
        <Icon role="decorative" type="solid" name="UserIcon" className="shrink-0 w-4 h-4 text-gray-500" />
        {name 
          ? <span className="whitespace-nowrap overflow-hidden">{name}</span> 
          : <span className="whitespace-nowrap overflow-hidden text-sm text-gray-500">{L.review.reassign_new_assignee_placeholder}</span>}
      </div>
    );
  };

  return (
    <div>
      <Select
        label={'Assigned To:'}
        labelLocation="left-inline"
        description={L.review.reassign_new_assignee_description}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        disabled={loadingAssignees || reassigning || !uid}
        customButtonTextRenderer={renderAssigneeButton}
        searchable
        portal
        sort
      />
    </div>
  );
}

/** Button that opens a modal containing the applicant's payment history. */
export function ViewPaymentsButton({ uid }: { uid: string | undefined }) {
  const [open, setOpen] = useState(false);
  if (!uid) return null;
  return (
    <>
      <Button
        variant="secondary"
        size="md"
        className="!rounded-md !border-gray-300 !font-normal"
        icon={<Icon name="CurrencyDollarIcon" size="sm" role="decorative" />}
        onClick={() => setOpen(true)}
      >
        View Payments
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Payments" size="fullscreen">
        <ApplicantPaymentPage applicant={uid} />
      </Modal>
    </>
  );
}

/** Component to display applicant subsurveys with sending options */
export function ResendApplicantSubsurveys(props: { uid: string }) {
  const { toast } = useToast();
  const L = useLocalizedStrings();

  const [loading, setLoading] = useState(true);
  const [subsurveys, setSubsurveys] = useState<Array<{
    subsurveyNameTranslations: Record<string, string>;
    part: string;
    url?: string;
    send_link?: RichText;
  }>>([]);

  const getExposedSubsurveys = usePost('/applicant/get_exposed_subsurveys');

  useEffect(() => {
    (async () => {
      if (!props.uid) {
        setLoading(false);
        return;
      }

      try {
        const result = await getExposedSubsurveys({ applicant: props.uid });
        if (result && result.error) {
          toast({
            description: "Could not load subsurveys",
            variant: 'error'
          });
        } else {
          setSubsurveys(result?.subsurveys || []);
        }
      } catch (error) {
        toast({
          description: "Could not load subsurveys",
          variant: 'error'
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [props.uid]);

  if (loading) {
    return (
      <div className="p-4">
        <SpacedSpinner />
      </div>
    );
  }

  return (
    <div>
      {subsurveys.length > 0 ? (
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">{L.applicant.resend_subsurveys.title}</h3>
          <p className="mt-2 max-w-4xl text-sm text-gray-600">
            {L.applicant.resend_subsurveys.available_subsurveys}
          </p>
          <SubsurveyList uid={props.uid} subsurveys={subsurveys} showHeader={false} onApplicantPage={true} />
        </div>
      ) : (
        <p>{L.applicant.resend_subsurveys.no_subsurveys_found}</p>
      )}
    </div>
  );
}

/** Circle indicator for review nav steps (number or check). Used as Button icon. */
export function StepCircleIndicator(props: {
  stepNumber: number;
  isActive: boolean;
  isCompleted: boolean;
  placement: 'left' | 'center';
}) {
  const { stepNumber, isActive, isCompleted, placement } = props;
  const wrapperClass = placement === 'left' ? 'flex-shrink-0 mr-2' : 'flex-shrink-0';

  if (isCompleted && !isActive) {
    return (
      <div className={wrapperClass} aria-hidden>
        <Icon
          name="CheckCircleIcon"
          type="solid"
          size="sm"
          color="var(--ak-identity-color-green-3)"
          role="decorative"
        />
      </div>
    );
  }

  return (
    <div
      className={`
        ${wrapperClass} w-[18px] h-[18px] rounded-full flex items-center justify-center text-xs
        ${isActive
      ? 'text-white'
      : 'text-gray-400 border border-gray-300'
    }
      `}
      style={isActive ? { backgroundColor: 'var(--ak-theme-color-button-primary-brand-background)' } : undefined}
      aria-hidden
    >
      <span className="text-xs leading-none flex items-center justify-center w-full h-full select-none tabular-nums">
        {stepNumber}
      </span>
    </div>
  );
}

interface ReviewWrapperProps {
  children: React.ReactNode;
}

export function ReviewWrapper({ children }: ReviewWrapperProps) {
  const panelContext = useContext(ResizablePanelControlContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const { info } = useContext(ApplicantContext);
  const { tags } = useContext(UserInfoContext);

  const L = useLocalizedStrings();

  const applicantFacingLogo = publicConfig.interface?.applicantFacingLogo?.url;
  const applicantFacingLogoWidth = publicConfig.interface?.applicantFacingLogo?.width || 150;
  // Only show the header when the review nav is visible (when the left nav is hidden)
  const showHeader = panelContext && !panelContext.isLeftNavVisible;

  const isAdminPanelPage = useLocation().pathname.includes('/admin-panel/');
  // Extract uid, action options, and current assignee from children props
  const uid = React.isValidElement(children) ? children.props.uid : undefined;
  const reviewDetails = React.isValidElement(children) ? children.props.review : undefined;
  const reassignReviewerConfig = reviewDetails?.reassignReviewer;
  const resolvedField = resolveAssigneeField(reassignReviewerConfig?.assigneeField, info || {});
  const currentAssigneeUid = resolvedField && info?.[resolvedField]
    ? String(info[resolvedField]).trim()
    : undefined;

  return (
    <div className="flex flex-col h-full">
      {!isAdminPanelPage && (
        <div className="flex justify-between items-center py-2 px-4 pr-2 border-b border-gray-200">
          <div className="flex-shrink-0">
            {showHeader && (
              <>
                {applicantFacingLogo ? (
                  <img
                    src={applicantFacingLogo}
                    width={applicantFacingLogoWidth}
                    alt="Organization logo"
                  />
                ) : (
                  <AidKitLogo width={150} />
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <AssigneeSelect
              uid={uid}
              reassignReviewerConfig={reassignReviewerConfig}
              currentAssigneeUid={currentAssigneeUid}
              info={info || {}}
            />
            {reviewDetails?.includePayments
              && (tags?.includes('reviewer') || tags?.includes('admin') || tags?.includes('all-applicants.view_payments'))
              && <ViewPaymentsButton uid={uid} />}
            {panelContext && panelContext.hasRightNavContent && (
              <Button
                onClick={() => (panelContext.isRightNavVisible ? panelContext.hideRightNav() : panelContext.showRightNav())}
                title={panelContext.isRightNavVisible ? 'Close applicant info panel' : 'Open applicant info panel'}
                aria-label={panelContext.isRightNavVisible ? 'Close applicant info panel' : 'Open applicant info panel'}
                icon={panelContext.isRightNavVisible
                  ? <Icon name='ChevronRightIcon' className="!h-4 !w-4 shrink-0" role='decorative' strokeWidth={1} />
                  : <Icon name='ChevronLeftIcon' className="!h-4 !w-4 shrink-0" role='decorative' strokeWidth={1} />}
                iconPlacement="right"
                variant="secondary"
                size="md"
                className="!rounded-md !border-gray-300 !font-normal"
              >
                {panelContext.isRightNavVisible ? L.admin_panel.reviews.hide_tools : L.admin_panel.reviews.show_tools}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
}

export function showV2Review(reviewConfiguration?: ReviewConfiguration, userEmail?: string) {
  return !!reviewConfiguration
    && (!reviewConfiguration.showV2ToUsers ||
    (!!userEmail
    && reviewConfiguration.showV2ToUsers.includes(userEmail)));
}

export type ReviewDetails = ReviewConfiguration & {
  title: string | v0.Text;
}

export type ReviewNavStep = {
  id: string;
  title: string;
  completed?: boolean;
  progressed?: boolean;
  visitable?: boolean;
  visible: boolean;
  children?: JSX.Element;
}

export type ReviewNavProps = {
  steps: ReviewNavStep[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  sequential: boolean;
  title: v0.Text | string;
  blockedEditBanner?: v0.RichText | string,
  info?: InfoDict,
  reviewDetails?: ReviewDetails,
  uid?: string,
}

export function ReviewNav(props: ReviewNavProps) {
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const panelContext = useContext(ResizablePanelControlContext);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [headerCardCollapsed, setHeaderCardCollapsed] = useState(
    !props.reviewDetails?.expandApplicantInfoByDefault,
  );
  const headerRef = React.useRef<HTMLDivElement>(null);
  const history = useHistory();
  const location = useLocation();

  // Back target is stored in React Router location state (not a `back` URL param)
  // to keep review URLs short - see how it's set in AdminPanelApplicantPage's
  // handleSubsurveyChange and the My Reviews table entry link. It's absent after
  // a hard refresh (location state doesn't survive reloads), in which case the
  // Back button falls back to reopening the left nav below.
  const backUrl = (location.state as { back?: string } | undefined)?.back;

  const blockedBanner = props.blockedEditBanner
    ? typeof props.blockedEditBanner === 'object'
      ? props.blockedEditBanner[context.lang] || props.blockedEditBanner['en']
      : props.blockedEditBanner
    : undefined;
  const blockedBannerMarkdown = useModularMarkdown({
    content: blockedBanner || '',
    info: props.info || {},
  });

  const isLeftNavVisible = panelContext?.isLeftNavVisible ?? false;

  const title = typeof props.title === 'string'
    ? props.title
    : props.title[context.lang] || props.title['en'];

  // Observe header height changes for dynamic positioning of sidebar nav buttons
  useEffect(() => {
    if (headerRef.current && panelContext?.setHeaderHeight) {
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (panelContext?.setHeaderHeight) {
            panelContext.setHeaderHeight(entry.contentRect.height);
          }
        }
      });

      resizeObserver.observe(headerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [panelContext]);

  return (
    <div className="flex h-full">
      {/* Vertical Sidebar Navigation */}
      <div className={`${isCollapsed ? 'w-15' : 'w-64'} border-r border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0 transition-all ${isLeftNavVisible ? 'hidden' : ''}`}>
        <div className={`p-2.5 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
          {panelContext && !isCollapsed && (
            <Button
              variant="unstyled"
              onClick={() => {
                if (backUrl) {
                  history.push(backUrl);
                } else {
                  panelContext.showLeftNav();
                }
              }}
              className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-200 text-gray-700 border border-gray-300 transition-colors"
              title="Show navigation"
              aria-label="Show navigation"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">{L.applicant.back}</span>
              </div>
            </Button>
          )}
          {!isCollapsed && (
            <Button
              variant="unstyled"
              onClick={() => setIsCollapsed(true)}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors flex justify-center"
              title="Collapse navigation"
              aria-label="Collapse navigation"
            >
              <Icon name="ChevronDoubleLeftIcon" type="solid" size="sm" role="decorative" />
            </Button>
          )}
          {isCollapsed && (
            <Button
              variant="unstyled"
              onClick={() => setIsCollapsed(false)}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors flex justify-center"
              title="Expand navigation"
              aria-label="Expand navigation"
            >
              <Icon name="ChevronDoubleRightIcon" type="solid" size="sm" role="decorative" />
            </Button>
          )}
        </div>
        {!isCollapsed && (
          <div className="py-2 px-4 text-xs uppercase text-gray-400 font-bold">{title}</div>
        )}
        <nav className={`p-2.5 ${isCollapsed ? 'flex flex-col items-center gap-2' : ''}`}>
          {props.steps.filter((s) => s.visible).map((step, filteredIndex) => {
            const originalIndex = props.steps.indexOf(step);
            const isActive = props.currentStep === originalIndex;
            const isCompleted = step.completed;
            const isVisitable = step.visitable;
            
            return (
              <Button
                key={`vertical-step-${originalIndex}`}
                onClick={() => {
                  if (isVisitable) {
                    props.setCurrentStep(originalIndex);
                  }
                }}
                variant={'unstyled'}
                disabled={!isVisitable}
                style={{
                  justifyContent: !isCollapsed ? 'start' : 'center',
                  ...(isActive
                    ? {
                      color: 'var(--ak-theme-color-button-primary-brand-background)',
                    }
                    : {}),
                }}
                className={`
                  w-full text-left p-2 mb-1 mx-auto rounded-md transition-colors font-normal
                  ${isActive 
                ? 'border font-medium shadow-sm' 
                : isVisitable 
                  ? 'hover:bg-gray-100 text-gray-700' 
                  : 'text-gray-400 bg-gray-50'
              }
                  ${isCompleted && !isActive ? 'text-gray-600' : ''}
                `}
                icon={props.sequential ? (
                  <StepCircleIndicator
                    stepNumber={filteredIndex + 1}
                    isActive={isActive}
                    isCompleted={!!isCompleted}
                    placement={isCollapsed ? 'center' : 'left'}
                  />
                ) : undefined}
                iconPlacement={isCollapsed ? 'none' : 'left'}
              >
                {!isCollapsed && (
                  <span 
                    style={{
                      color: isActive ? 'var(--ak-theme-color-button-primary-brand-background)' : 'inherit',
                    }}
                    className={`text-sm font-normal ${isActive ? '' : 'text-gray-600'}`}
                  >
                    {step.title}
                  </span>
                )}
              </Button>
            );
          })}
        </nav>

      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {props.reviewDetails && (
          <div ref={headerRef}>
            <ApplicantHeaderCard
              pinnedApplicantInfo={props.reviewDetails.pinnedApplicantInfo}
              uid={props.uid}
              applicationState={props.reviewDetails.applicationState}
              isCollapsed={headerCardCollapsed}
              onCollapseChange={setHeaderCardCollapsed}
            />
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {blockedBanner && (
            <div className="flex rounded-md bg-yellow-50 border-2 border-yellow-300 p-2 justify-around m-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="ExclamationTriangleIcon" type="solid" size="md" className="text-yellow-400" role="decorative" />
                </div>
                <div className="ml-3">
                  <div className="text-sm text-yellow-700 -mb-4">
                    <div>{blockedBannerMarkdown}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {props.steps[props.currentStep]?.children}
        </div>
      </div>
    </div>
  );
}

